import { PropsWithChildren, useMemo, useState } from 'react';
import {
	SecurityFeatureBaseProps,
	buildSecurityRankingDeepLink,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { FormProvider } from 'react-hook-form';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';

import { useMutation } from '@tanstack/react-query';
import {
	deleteSecurityExchangeRights,
	updateSecurityExchangeRights,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';

import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import { NumberInput, Select } from 'react-hook-form-mantine';

import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import {
	EXCHANGE_RIGHTS_FORM_FIELD,
	getExchangeRightsPeriodBasisLabel,
	getExchangeRightsTransactionTypeLabel,
	makeExchangeRightsPeriodBasisOptions,
	makeExchangeRightsTransactionTypeOptions,
	useExchangeRightsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ExchangeRights/useExchangeRightsController.ts';
import { SecurityExchangeRightsFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ExchangeRights/exchangeRights.zod.ts';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput.tsx';

import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { LONG_DASH, NOT_APPLICABLE } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';

import { numberToUiPercent } from '@dealstack/shared/lib/format/number.ts';

import {
	getPeriodDateType,
	getPeriodType,
	makePeriodTimeDateOptions,
	makePeriodTimeUnitOptions,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/period-dropdown-options.tsx';

import {
	getDayCountConventionTranslation,
	makeDayCountConventionDropdownOptions,
} from '@dealstack/frontend/shared/incomeRights.ts';
import {
	type PeriodDateType,
	SecurityExchangeRightsPeriodBasis,
	SecurityExchangeTransactionType,
	SecurityFeatureType,
} from '@dealstack/shared/lib/generated';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { ExchangeRightsStyles } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ExchangeRights/ExchangeRights.styles.tsx';
import { Text } from '@mantine/core';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';
import { MixedTextDropdownInput } from '@dealstack/frontend/shared/components/MixedTextDropdownInput/MixedTextDropdownInput.tsx';
import { convertCurrencyToSymbol } from '@dealstack/shared/lib/format/currency/currency.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { SecurityRankField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityRankField/SecurityRankField.tsx';
import { useNavigate } from 'react-router-dom';

const getIntervalDuration = (
	periodBasis: SecurityExchangeRightsPeriodBasis,
	allowedBases: SecurityExchangeRightsPeriodBasis[],
	duration?: number | string | null
): number | null => {
	if (!allowedBases.includes(periodBasis)) {
		return null;
	}

	return duration ? Number(duration) : null;
};

export const ExchangeRights = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
	relativeRanksMap,
}: PropsWithChildren<SecurityFeatureBaseProps>) => {
	const tCommonUI = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const dayCountConventionOptions = makeDayCountConventionDropdownOptions(tCommonUI);
	const { exchangeRightsForm, dealSecurities, resetForm, isAdditionalPriceRateZeroOrEmpty } =
		useExchangeRightsController(security);
	const [isEdit, setIsEdit] = useState(!security?.securityExchangeRights);
	const navigate = useNavigate();
	const navigateToRanking = () =>
		navigate(buildSecurityRankingDeepLink(security?.id, SecurityFeatureType.OPTION));

	const formHasCompoundPeriodValue = !!exchangeRightsForm.watch(
		EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceCompoundingPeriod
	);

	const deleteExchangeRightsRequest = useMutation({
		mutationFn: deleteSecurityExchangeRights,
	});
	const updateExchangeRightsRequest = useMutation({
		mutationFn: updateSecurityExchangeRights,
	});

	const exchangedForSecurity = useMemo(() => {
		return (
			dealSecurities
				?.filter((dealSecurity) =>
					security?.securityExchangeRights?.exchangedForSecurityId?.includes(
						dealSecurity.value
					)
				)
				?.map((dealSecurity) => dealSecurity.label)
				?.join(', ') || LONG_DASH
		);
	}, [dealSecurities, security?.securityExchangeRights?.exchangedForSecurityId]);

	const isLoading =
		updateExchangeRightsRequest.isPending || deleteExchangeRightsRequest.isPending;

	const transactionType = exchangeRightsForm.watch(EXCHANGE_RIGHTS_FORM_FIELD.transactionType);
	const isTransferType = transactionType === SecurityExchangeTransactionType.TRANSFER;
	const periodBasis = exchangeRightsForm.watch(EXCHANGE_RIGHTS_FORM_FIELD.periodBasis);

	const periodBasisDateFieldDisabled =
		periodBasis !== SecurityExchangeRightsPeriodBasis.FIXED_DATE;
	const periodBasisIntervalStartDurationDisabled =
		periodBasis !== SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE;
	const periodBasisIntervalEndDurationDisabled =
		periodBasis === SecurityExchangeRightsPeriodBasis.UNRESTRICTED;

	const securityRank = useMemo(() => {
		if (!security || !isTransferType) {
			return null;
		}

		const matchedSecRank = security.securityRanks?.find(
			(secRank) => secRank.featureType === SecurityFeatureType.OPTION
		);

		return matchedSecRank
			? {
					...matchedSecRank,
					relativeSortOrder: relativeRanksMap?.get(matchedSecRank.rankId)
						?.relativeSortOrder,
				}
			: null;
	}, [security, relativeRanksMap, isTransferType]);

	const performUpdateExchangeRights = (args: SecurityExchangeRightsFormDto) => {
		const securityId = security?.id ?? '';
		const basis = args.periodBasis;
		const isArgFixedDate = basis === SecurityExchangeRightsPeriodBasis.FIXED_DATE;
		const isArgRelative = basis === SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE;

		const periodBasisDate = isArgFixedDate ? args.periodBasisDate : null;
		const periodBasisIntervalStartDuration = getIntervalDuration(
			basis,
			[SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE],
			args.periodBasisIntervalStartDuration
		);
		const periodBasisIntervalStartPeriod = isArgRelative
			? args.periodBasisIntervalStartPeriod
			: null;
		const periodBasisIntervalEndDuration = getIntervalDuration(
			basis,
			[
				SecurityExchangeRightsPeriodBasis.FIXED_DATE,
				SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE,
			],
			args.periodBasisIntervalEndDuration
		);
		const periodBasisIntervalEndPeriod =
			isArgRelative || isArgFixedDate ? args.periodBasisIntervalEndPeriod : null;

		updateExchangeRightsRequest.mutate(
			{
				securityId,
				body: {
					...args,
					periodBasisDate,
					periodBasisIntervalStartDuration,
					periodBasisIntervalStartPeriod,
					periodBasisIntervalEndDuration,
					periodBasisIntervalEndPeriod,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.exchange_rights_updated_successfully'),
					});

					refreshDetails?.(securityId);
					setIsEdit(false);
				},
			}
		);
	};

	const handleDeleteExchangeRights = () => {
		const securityId = security?.id ?? '';
		deleteExchangeRightsRequest.mutate(securityId, {
			onSuccess: () => {
				showToastV2({
					title: tCapManage('toasts.success.exchange_rights_deleted_successfully'),
				});
				refreshDetails?.(securityId);
				setIsEdit(false);
			},
		});
	};

	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	const makePeriodDateLabel = (
		periodType: PeriodDateType | undefined,
		duration: number | undefined
	): string => {
		if (!periodType || !duration) {
			return LONG_DASH;
		}

		const periodDateType = getPeriodDateType(tCommonUI, periodType, duration);
		const durationWithPeriod = `${duration} ${periodDateType}`;

		if (
			security?.securityExchangeRights?.periodBasis ===
			SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE
		) {
			return `${durationWithPeriod} ${tCapManage('securities.period_date_after.label')}`;
		}

		return durationWithPeriod;
	};

	return (
		<EditableInfoContainer
			title={tCapManage('securities.exchange_rights.title')}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={exchangeRightsForm.handleSubmit(performUpdateExchangeRights)}
			onRemove={handleDeleteExchangeRights}
			onCancel={handleCancel}
			hideRemove={!security?.securityExchangeRights?.id}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
		>
			<ExchangeRightsStyles>
				<FormProvider {...exchangeRightsForm}>
					{isLoading && <InlineLoader />}

					<form
						onSubmit={exchangeRightsForm.handleSubmit(performUpdateExchangeRights)}
						style={{ width: '100%' }}
					>
						<EditableInfoContainerGrid $gridColumns={4} $alignItems="start">
							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.transaction_type.label'
								)}
								formInput={
									<Select
										name={EXCHANGE_RIGHTS_FORM_FIELD.transactionType}
										control={exchangeRightsForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={makeExchangeRightsTransactionTypeOptions(tCommonUI)}
										onChange={() => {
											exchangeRightsForm.setValue(
												EXCHANGE_RIGHTS_FORM_FIELD.exchangedForSecurityId,
												''
											);
										}}
									/>
								}
								data={getExchangeRightsTransactionTypeLabel(
									tCommonUI,
									security?.securityExchangeRights?.transactionType
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage('securities.exchange_rights.exchanged_for.label')}
								formInput={
									<Select
										name={EXCHANGE_RIGHTS_FORM_FIELD.exchangedForSecurityId}
										control={exchangeRightsForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={dealSecurities}
									/>
								}
								data={exchangedForSecurity}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage('securities.exchange_rights.exchange_rate.label')}
								formInput={
									<NumberInput
										name={EXCHANGE_RIGHTS_FORM_FIELD.rate}
										control={exchangeRightsForm.control}
										size="md"
										hideControls
										leftSection={
											<Text size="lg" c={defaultTheme.v2.color.neutral[600]}>
												1:
											</Text>
										}
									/>
								}
								data={security?.securityExchangeRights?.rate ?? 1}
							/>

							{isTransferType && (
								<EditableInfoField
									editMode={isEdit}
									label={tCapManage(
										'securities.exchange_rights.option_rank.label'
									)}
									formInput={
										<SecurityRankField
											unranked={!securityRank}
											rankName={securityRank?.rank.displayName}
											sortOrder={securityRank?.relativeSortOrder || 1}
											totalRanks={relativeRanksMap?.size}
											onClick={navigateToRanking}
										/>
									}
									customValueElement={
										<SecurityRankField
											unranked={!securityRank}
											rankName={securityRank?.rank.displayName}
											sortOrder={securityRank?.relativeSortOrder || 1}
											totalRanks={relativeRanksMap?.size}
											showContainer={false}
										/>
									}
								/>
							)}
						</EditableInfoContainerGrid>

						<Text fz="sm" fw={600} className="separator">
							{tCapManage(
								'securities.exchange_rights.separator_label.exercise_period'
							)}
						</Text>

						<EditableInfoContainerGrid $gridColumns={4} $alignItems="start">
							<DetailEditableField
								editMode={isEdit}
								label={tCapManage('securities.exchange_rights.period_basis.label')}
								formInput={
									<Select
										name={EXCHANGE_RIGHTS_FORM_FIELD.periodBasis}
										control={exchangeRightsForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={makeExchangeRightsPeriodBasisOptions(tCommonUI)}
									/>
								}
								data={getExchangeRightsPeriodBasisLabel(
									tCommonUI,
									security?.securityExchangeRights?.periodBasis
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.period_basis_date.label'
								)}
								formInput={
									<MantineDateInput
										name={EXCHANGE_RIGHTS_FORM_FIELD.periodBasisDate}
										size="md"
										clearable
										disabled={periodBasisDateFieldDisabled}
										onChange={(value) => {
											const isoDate = browserDateToMidnightUTC(value) ?? '';

											exchangeRightsForm.setValue(
												EXCHANGE_RIGHTS_FORM_FIELD.periodBasisDate,
												isoDate
											);
										}}
									/>
								}
								data={
									periodBasisDateFieldDisabled
										? LONG_DASH
										: formatStringDateToUI(
												security?.securityExchangeRights?.periodBasisDate,
												DATE_FORMATS.MONTH_DAY_YEAR
											)
								}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.period_interval_start.label'
								)}
								formInput={
									<MixedTextDropdownInput
										inputName={
											EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalStartDuration
										}
										selectName={
											EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalStartPeriod
										}
										control={exchangeRightsForm.control}
										selectData={makePeriodTimeDateOptions(tCommonUI)}
										selectPlaceholder={NOT_APPLICABLE}
										disabled={periodBasisIntervalStartDurationDisabled}
									/>
								}
								data={makePeriodDateLabel(
									security?.securityExchangeRights
										?.periodBasisIntervalStartPeriod,
									security?.securityExchangeRights
										?.periodBasisIntervalStartDuration
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.period_interval_end.label'
								)}
								formInput={
									<MixedTextDropdownInput
										inputName={
											EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalEndDuration
										}
										selectName={
											EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalEndPeriod
										}
										control={exchangeRightsForm.control}
										selectData={makePeriodTimeDateOptions(tCommonUI)}
										selectPlaceholder={NOT_APPLICABLE}
										disabled={periodBasisIntervalEndDurationDisabled}
									/>
								}
								data={makePeriodDateLabel(
									security?.securityExchangeRights?.periodBasisIntervalEndPeriod,
									security?.securityExchangeRights?.periodBasisIntervalEndDuration
								)}
							/>
						</EditableInfoContainerGrid>

						<Text fz="sm" fw={600} className="separator">
							{tCapManage(
								'securities.exchange_rights.separator_label.exercise_price'
							)}
						</Text>

						<EditableInfoContainerGrid $gridColumns={4} $alignItems="start">
							<DetailEditableField
								editMode={isEdit}
								label={tCapManage('securities.exchange_rights.price.label')}
								formInput={
									<NumberInput
										name={EXCHANGE_RIGHTS_FORM_FIELD.price}
										control={exchangeRightsForm.control}
										size="md"
										hideControls
										decimalScale={2}
										leftSection={
											<Text size="lg" c={defaultTheme.v2.color.neutral[600]}>
												{convertCurrencyToSymbol(security?.currency)}
											</Text>
										}
									/>
								}
								data={security?.securityExchangeRights?.price ?? LONG_DASH}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.additional_price_rate.label'
								)}
								formInput={
									<NumberInput
										name={EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceRate}
										control={exchangeRightsForm.control}
										size="md"
										decimalScale={3}
										rightSection={<Icon variant="percentage" size="vlg" />}
										className="percentage-input"
									/>
								}
								data={numberToUiPercent(
									security?.securityExchangeRights?.additionalPriceRate,
									true,
									2
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.accrual_period.label'
								)}
								formInput={
									<Select
										name={
											EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceAccrualPeriod
										}
										control={exchangeRightsForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={makePeriodTimeUnitOptions(tCommonUI)}
										disabled={isAdditionalPriceRateZeroOrEmpty}
									/>
								}
								data={getPeriodType(
									tCommonUI,
									security?.securityExchangeRights?.additionalPriceAccrualPeriod
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.day_count_convention.label'
								)}
								formInput={
									<Select
										name={
											EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceDayCountConvention
										}
										control={exchangeRightsForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={dayCountConventionOptions.map((o) => ({
											value: o.value,
											label: o.label ?? '',
										}))}
										disabled={isAdditionalPriceRateZeroOrEmpty}
									/>
								}
								data={getDayCountConventionTranslation(
									tCommonUI,
									security?.securityExchangeRights
										?.additionalPriceDayCountConvention
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.deemed_start_date.label'
								)}
								formInput={
									<MantineDateInput
										name={
											EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceDeemedStartDate
										}
										onChange={(value) => {
											const isoDate = browserDateToMidnightUTC(value) ?? '';

											exchangeRightsForm.setValue(
												EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceDeemedStartDate,
												isoDate
											);
										}}
										size="md"
										clearable
										disabled={isAdditionalPriceRateZeroOrEmpty}
									/>
								}
								data={formatStringDateToUI(
									security?.securityExchangeRights
										?.additionalPriceDeemedStartDate,
									DATE_FORMATS.MONTH_DAY_YEAR
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.compounding_period.label'
								)}
								formInput={
									<Select
										name={
											EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceCompoundingPeriod
										}
										control={exchangeRightsForm.control}
										size="md"
										rightSection={
											formHasCompoundPeriodValue ? undefined : (
												<Icon variant="caret-down" size="lg" />
											)
										}
										rightSectionProps={
											formHasCompoundPeriodValue
												? undefined
												: {
														style: { pointerEvents: 'none' },
													}
										}
										data={makePeriodTimeUnitOptions(tCommonUI)}
										allowDeselect
										clearable
										placeholder={tCommonUI(
											'common_placeholders.select_period_unit'
										)}
										disabled={isAdditionalPriceRateZeroOrEmpty}
									/>
								}
								data={getPeriodType(
									tCommonUI,
									security?.securityExchangeRights
										?.additionalPriceCompoundingPeriod
								)}
							/>

							<DetailEditableField
								editMode={isEdit}
								label={tCapManage(
									'securities.exchange_rights.first_interest_date.label'
								)}
								formInput={
									<MantineDateInput
										name={
											EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceFirstInterestDate
										}
										onChange={(value) => {
											const isoDate = browserDateToMidnightUTC(value) ?? '';

											exchangeRightsForm.setValue(
												EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceFirstInterestDate,
												isoDate
											);
										}}
										size="md"
										clearable
										disabled={isAdditionalPriceRateZeroOrEmpty}
									/>
								}
								data={formatStringDateToUI(
									security?.securityExchangeRights
										?.additionalPriceFirstInterestDate,
									DATE_FORMATS.MONTH_DAY_YEAR
								)}
							/>
						</EditableInfoContainerGrid>
					</form>
				</FormProvider>
			</ExchangeRightsStyles>
		</EditableInfoContainer>
	);
};
